import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  CircularProgress,
  MenuItem,
  InputLabel,
  FormControl,
  Select,
  Alert,
  ListSubheader,
  Box,
  IconButton,
  FormControlLabel,
  Switch
} from "@mui/material";
import { PhotoCamera, Cancel as CancelIcon } from '@mui/icons-material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import agentService from "../../services/agentService";
import partenaireService from "../../services/partenaireServices";
import useAuthStore from "../../store/authStore";

const ROLES_BY_TYPE = {
  admin_clinique: {
    label: "CLINIQUE",
    roles: {
      admin: "admin_clinique",
      agent: "agent_clinique",
      medecin: "medecin_clinique"
    }
  },
  admin_laboratoire: {
    label: "LABORATOIRE",
    roles: {
      admin: "admin_laboratoire",
      agent: "agent_laboratoire"
    }
  },
  admin_pharmacie: {
    label: "PHARMACIE",
    roles: {
      admin: "admin_pharmacie",
      agent: "agent_pharmacie"
    }
  },
  admin_clinique_laboratoire: {
    label: "CLINIQUE_LABORATOIRE",
    roles: {
      admin: "admin_clinique_laboratoire",
      agent: "agent_clinique_laboratoire",
      medecin: "medecin_clinique_laboratoire"
    }
  }
};

const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const PHONE_REGEX = /^\+?[0-9\s-]*$/;

const RegisterPersonnel = ({ onRefresh, filterRole }) => {
  const { user } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [partenaires, setPartenaires] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    email: "",
    matricule: "",
    role: "",
    partenaire_id: user?.partie_id || "",
    tel: "",
    genre: "",
    password: "",
    statut: true,
    image: null
  });

  const [validationErrors, setValidationErrors] = useState({
    nom: false,
    prenom: false,
    email: false,
    role: false,
    password: false,
    tel: false,
    genre: false
  });

  const [passwordError, setPasswordError] = useState('');

  const handleClose = () => {
    setOpen(false);
    setError(null);
    setImagePreview(null);
    setFormData({
      nom: "",
      prenom: "",
      email: "",
      matricule: "",
      role: "",
      partenaire_id: user?.partie_id || "",
      tel: "",
      genre: "",
      password: "",
      statut: true,
      image: null
    });
    setValidationErrors({
      nom: false,
      prenom: false,
      email: false,
      role: false,
      password: false,
      tel: false,
      genre: false
    });
  };

  const getAvailableRoles = () => {
    const userRole = user?.role;
    let roles = [];
    
    if (userRole in ROLES_BY_TYPE) {
      const roleType = ROLES_BY_TYPE[userRole];
      roles.push({
        category: roleType.label,
        roles: Object.entries(roleType.roles).filter(([key, value]) => 
          !filterRole || value === filterRole
        )
      });
    }
    
    return roles;
  };

  useEffect(() => {
    if (open) {
      const fetchPartenaires = async () => {
        try {
          setLoading(true);
          const response = await partenaireService.getAllPartenaires();
          setPartenaires(response.data);
        } catch (err) {
          setError("Erreur lors du chargement des partenaires");
        } finally {
          setLoading(false);
        }
      };
      fetchPartenaires();
    }
  }, [open]);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateForm = () => {
    const errors = {
      nom: !formData.nom,
      prenom: !formData.prenom,
      email: !validateEmail(formData.email),
      role: !formData.role,
      password: formData.password.length < 8,
      tel: formData.tel ? !PHONE_REGEX.test(formData.tel) : false,
      genre: !formData.genre
    };

    setPasswordError(errors.password ? "Minimum 8 caractères requis" : '');
    setValidationErrors(errors);
    return !Object.values(errors).some(error => error);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Veuillez remplir correctement tous les champs requis");
      return;
    }

    // Ajouter ce log pour debug
    console.log('FormData being submitted:', formData);

    try {
      setSubmitting(true);
      await agentService.createAgent(formData);
      toast.success("Agent créé avec succès");
      onRefresh();
      handleClose();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Erreur lors de la création de l'agent";
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      setError("La taille de l'image ne doit pas dépasser 5MB");
      return;
    }

    setFormData(prev => ({ ...prev, image: file }));
    setImagePreview(URL.createObjectURL(file));
  };

  const renderRoleOptions = () => 
    getAvailableRoles().map(({ category, roles }) => [
      <ListSubheader key={category} sx={{ bgcolor: 'background.paper' }}>
        {category.replace('_', ' ')}
      </ListSubheader>,
      roles.map(([key, value]) => (
        <MenuItem key={value} value={value}>
          {value.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
        </MenuItem>
      ))
    ]);

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      
      <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
        Ajouter un agent
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', py: 2 }}>
          Nouvel Agent
        </DialogTitle>

        <DialogContent sx={{ py: 4, mt: 2 }}>
          <Grid container spacing={3}>
            {error && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                label="Nom *"
                name="nom"
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                error={validationErrors.nom}
                helperText={validationErrors.nom && "Champ requis"}
                fullWidth
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Prénom *"
                name="prenom"
                value={formData.prenom}
                onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                error={validationErrors.prenom}
                helperText={validationErrors.prenom && "Champ requis"}
                fullWidth
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Email *"
                type="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                error={validationErrors.email}
                helperText={validationErrors.email && "Email invalide"}
                fullWidth
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Téléphone"
                name="tel"
                value={formData.tel}
                onChange={(e) => setFormData({ ...formData, tel: e.target.value })}
                error={validationErrors.tel}
                helperText={validationErrors.tel && "Format invalide"}
                fullWidth
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Mot de passe *"
                type="password"
                name="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                error={validationErrors.password}
                helperText={passwordError}
                fullWidth
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Rôle *</InputLabel>
                <Select
                  name="role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  error={validationErrors.role}
                  label="Rôle *"
                >
                  {renderRoleOptions()}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Matricule *"
                name="matricule"
                value={formData.matricule}
                onChange={(e) => setFormData({ ...formData, matricule: e.target.value })}
                error={validationErrors.matricule}
                helperText={validationErrors.matricule && "Champ requis"}
                fullWidth
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Genre *</InputLabel>
                <Select
                  name="genre"
                  value={formData.genre}
                  onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                  error={validationErrors.genre}
                  label="Genre *"
                >
                  <MenuItem value="homme">Homme</MenuItem>
                  <MenuItem value="femme">Femme</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={3}>
                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                  <Box
                    sx={{
                      width: 150,
                      height: 150,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      border: '2px solid',
                      borderColor: 'primary.main',
                      bgcolor: 'background.default'
                    }}
                  >
                    {imagePreview && (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    )}
                  </Box>
                  {imagePreview && (
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        bgcolor: 'background.paper',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                      onClick={() => {
                        setImagePreview(null);
                        setFormData({ ...formData, image: null });
                      }}
                    >
                      <CancelIcon fontSize="small" color="error" />
                    </IconButton>
                  )}
                </Box>
                <input
                  accept="image/png, image/jpeg"
                  style={{ display: 'none' }}
                  id="image-upload"
                  type="file"
                  onChange={handleImageChange}
                />
                <label htmlFor="image-upload">
                  <Button
                    component="span"
                    variant="outlined"
                    startIcon={<PhotoCamera />}
                    sx={{ textTransform: 'none' }}
                  >
                    {imagePreview ? "Changer la photo" : "Ajouter une photo"}
                  </Button>
                </label>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.statut}
                    onChange={(e) => setFormData({ ...formData, statut: e.target.checked })}
                    color="primary"
                  />
                }
                label="Compte actif"
                labelPlacement="start"
                sx={{ ml: 0 }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
          <Button 
            onClick={handleClose} 
            color="inherit"
            sx={{ mr: 2 }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={submitting}
            startIcon={submitting && <CircularProgress size={20} color="inherit" />}
          >
            {submitting ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RegisterPersonnel;